import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PendingApprovalRow from "./PendingApprovalRow";
import RejectReasonModal from "./RejectReasonModal";
import {
  fetchPendingApprovals,
  approveUser,
  rejectUser,
  setFilters,
} from "../../../features/admin/adminSlice";

const PendingApprovalTable = ({ filterByRole }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const {
    pendingApprovals,
    pagination,
    filters,
    loading,
    error,
    approvalLoading,
  } = useSelector((state) => state.admin);

  useEffect(() => {
    const { sortBy, sortOrder, ...restFilters } = filters;
    const params = {
      ...restFilters,
      role: filterByRole !== "all" ? filterByRole : filters.role,
    };
    dispatch(fetchPendingApprovals(params));
  }, [dispatch, filterByRole, filters]);

  const handleApprove = (id) => {
    dispatch(approveUser(id));
  };

  const handleReject = (id, userName) => {
    setSelectedUser({ id, name: userName });
    setRejectModalOpen(true);
  };

  const handleConfirmReject = (reason) => {
    if (selectedUser) {
      dispatch(rejectUser({ id: selectedUser.id, reason }));
      setSelectedUser(null);
    }
  };

  const handleViewProfile = (id, role) => {
    const routeMap = {
      player: `/admin/player/${id}`,
      coach: `/admin/coach/${id}`,
      scout: `/admin/scout/${id}`,
    };

    const route = routeMap[role.toLowerCase()];
    if (route) {
      navigate(route);
    }
  };

  const handlePageChange = (newPage) => {
    dispatch(setFilters({ page: newPage }));
  };

  if (error) {
    return (
      <div className="rounded-3xl bg-jp-card border border-jp-line p-6">
        <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-jp-card border border-jp-line overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-jp-card">
            <tr className="border-b border-jp-line text-center">
              {["NAME", "EMAIL", "ROLE", "DOCUMENT", "SUBMITTED AT", "STATUS", "ACTIONS"].map(
                (h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-[11px] font-medium text-jp-muted uppercase text-center"
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {loading && pendingApprovals.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-jp-muted">
                  Loading...
                </td>
              </tr>
            ) : pendingApprovals.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-jp-muted">
                  No pending approvals found
                </td>
              </tr>
            ) : (
              pendingApprovals.map((approval) => (
                <PendingApprovalRow
                  key={approval._id}
                  approval={approval}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onViewProfile={handleViewProfile}
                  loading={approvalLoading[approval._id]}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-jp-line">
          <div className="text-xs text-jp-muted">
            Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of{" "}
            {pagination.totalCount} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="px-3 py-1 rounded bg-jp-section text-xs text-jp-ink hover:bg-jp-line disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-jp-muted">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasMore}
              className="px-3 py-1 rounded bg-jp-section text-xs text-jp-ink hover:bg-jp-line disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <RejectReasonModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        userName={selectedUser?.name || ''}
      />
    </div>
  );
};

export default PendingApprovalTable;
